/**
 * ValuesRowsGrid — compact grid of VALUES rows for the INSERT renderer. Per
 * § 1.4 each row ships a trailing `×`, and the grid footer ships a
 * `[+ add row]`. Header column labels default to `Col N` when no column list
 * is provided.
 *
 * VQE-Edit wiring: when `editContext` is supplied each cell becomes an
 * inline-editable `ValuesCellPill` (`modifyInsertValuesCell`, decision 3 —
 * free-text, gates on `!!editContext` alone per decision 7), the per-row `×`
 * commits a `removeInsertValuesRow` op, and the footer `[+ add row]` opens the
 * column-aware `ValuesRowAddPopover`. A cell padded past its row's actual
 * arity (mismatched row lengths) stays a plain span — there is nothing there
 * to edit (still tone-classified, DML-35). Without `editContext` the disabled
 * VQE-RO scaffolding renders unchanged.
 *
 * `engine` (DML-36, Advisor 2 §A7 third bullet) drives an honest, non-blocking
 * warning shown ONLY for `engine === 'oracle'` with more than one VALUES row:
 * the VQE has no INSERT-ALL emitter (`addInsertValuesRow` is engine-blind — it
 * just appends a `(…)` tuple), so a 2nd+ row produces a bare multi-row
 * `VALUES (…),(…)` that Oracle rejects. This never hard-blocks `[+ add row]`
 * — it's a hint, not a gate.
 */

import { TriangleAlert } from 'lucide-react';

import { AddButton } from '../common/AddButton';
import { RemoveButton } from '../common/RemoveButton';
import { VqeTooltip } from '../common/VqeTooltip';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { removeInsertValuesRow } from '../../edit/edits/removeInsertValuesRow';
import { ClauseCard } from '../select/ClauseCard';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { ValuesRow } from './extractInsertOutline';
import { ValuesRowAddPopover } from './ValuesRowAddPopover';
import { ValuesCellPill } from './ValuesCellPill';
import { classifyValueCellKind, VALUE_CELL_KIND_TONE_CLASS } from './valueCellKind';
import { vqeCopy } from '../../ux/vqeCopy';

const CELL_TEST_ID_PREFIX = 'vqe-insert-values-cell';

export interface ValuesRowsGridProps {
  readonly rows: readonly ValuesRow[];
  readonly columns: readonly string[] | null;
  readonly editContext?: VqeEditContext;
  /** Connection engine (Task 2/DML-36) — drives the Oracle multi-row-VALUES warning. */
  readonly engine?: SqlEngine;
}

export function ValuesRowsGrid({ rows, columns, editContext, engine }: ValuesRowsGridProps) {
  const columnCount = Math.max(
    columns?.length ?? 0,
    ...rows.map((r) => r.cells.length),
  );
  const headerLabels: string[] =
    columns && columns.length === columnCount
      ? [...columns]
      : Array.from({ length: columnCount }, (_, idx) => `Col ${idx + 1}`);

  const editEnabled = !!editContext;

  return (
    <ClauseCard
      title="Values"
      testId="vqe-insert-values"
      accessory={
        <span
          className="text-[11px] uppercase tracking-wide text-muted-foreground"
          data-testid="vqe-insert-values-row-count"
        >
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </span>
      }
      footer={
        editEnabled && rows.length > 0 ? (
          <ValuesRowAddPopover editContext={editContext} headerLabels={headerLabels} />
        ) : (
          <AddButton label={vqeCopy.actions.addRow} testId="vqe-insert-add-row" />
        )
      }
    >
      {engine === 'oracle' && rows.length > 1 ? (
        <div
          role="status"
          data-testid="vqe-insert-values-oracle-multirow-warning"
          className="mb-2 flex items-start gap-2 rounded-md border border-amber-500/60 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
        >
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">{vqeCopy.hint.oracleMultiRowValues}</span>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid="vqe-insert-values-empty">
          No VALUES rows parsed.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-xs"
            data-testid="vqe-insert-values-grid"
          >
            <thead>
              <tr>
                <th
                  scope="col"
                  className="border-b border-border px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                  data-testid="vqe-insert-values-row-number-header"
                >
                  Row
                </th>
                {headerLabels.map((label, idx) => (
                  <th
                    key={`${label}-${idx}`}
                    scope="col"
                    className="border-b border-border px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                    data-testid={`vqe-insert-values-header-${idx}`}
                  >
                    {label}
                  </th>
                ))}
                <th scope="col" className="border-b border-border px-2 py-1" aria-label="row actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={`${row.parenStart}-${rIdx}`} data-testid={`vqe-insert-values-row-${rIdx}`}>
                  <td
                    className="select-none border-b border-border/40 px-2 py-1 align-top text-[11px] font-medium text-muted-foreground"
                    data-testid={`vqe-insert-values-row-number-${rIdx}`}
                  >
                    Row {rIdx + 1}
                  </td>
                  {Array.from({ length: columnCount }, (_, cIdx) => {
                    const cell = row.cells[cIdx] ?? '';
                    // A cell padded past this row's actual arity (mismatched row
                    // lengths) has no underlying cell to edit — keep it a plain span
                    // even when editing is otherwise enabled.
                    const cellEditable = editEnabled && editContext && cIdx < row.cells.length;
                    return (
                      <td
                        key={`${rIdx}-${cIdx}`}
                        className="border-b border-border/40 px-2 py-1 align-top"
                        data-testid={`vqe-insert-values-cell-${rIdx}-${cIdx}`}
                      >
                        {cellEditable ? (
                          <ValuesCellPill
                            testIdPrefix={CELL_TEST_ID_PREFIX}
                            text={cell}
                            rowIndex={rIdx}
                            cellIndex={cIdx}
                            editContext={editContext}
                            columnName={columns?.[cIdx]}
                          />
                        ) : (
                          <VqeTooltip
                            content={editEnabled ? undefined : vqeCopy.disabled.editingUnavailable}
                          >
                            <span
                              className={`font-mono ${VALUE_CELL_KIND_TONE_CLASS[classifyValueCellKind(cell)]}`}
                              data-testid={`vqe-insert-values-cell-text-${rIdx}-${cIdx}`}
                              data-cell-kind={classifyValueCellKind(cell)}
                            >
                              {cell}
                            </span>
                          </VqeTooltip>
                        )}
                      </td>
                    );
                  })}
                  <td className="border-b border-border/40 px-2 py-1 text-right align-top">
                    <RemoveButton
                      testId={`vqe-insert-values-remove-${rIdx}`}
                      ariaLabel={`Remove row ${rIdx + 1}`}
                      disabled={!editEnabled}
                      tooltip={editEnabled ? `Remove row ${rIdx + 1}` : undefined}
                      onClick={
                        editEnabled && editContext
                          ? () => {
                              void removeInsertValuesRow(rIdx, toCommitCtx(editContext));
                            }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ClauseCard>
  );
}
